import React, { memo, useCallback, useMemo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import type { FieldMapping, SourceField } from '../types';
import { TARGET_FIELD_GROUPS } from '../types';

import classes from './fieldMappingEditor.module.css';

export interface KeyedFieldMapping extends FieldMapping {
  rowKey: number;
}

interface FieldMappingEditorProps {
  mappings: KeyedFieldMapping[];
  sourceFields: SourceField[];
  onAdd: () => void;
  onChange: (mappings: KeyedFieldMapping[]) => void;
}

const GROUP_LABELS: Record<string, string> = {
  contact: 'Contact',
  opportunity: 'Opportunity',
  custom: 'Custom Fields',
};

function FieldMappingEditor({ mappings, sourceFields, onAdd, onChange }: FieldMappingEditorProps) {
  const usedTargetFields = mappings.map((m) => m.target_field).filter(Boolean);
  const usedSourceKeys = mappings.map((m) => m.source_field_key).filter(Boolean);

  const groupedSourceFields = useMemo(() => {
    const groups: Record<string, SourceField[]> = {};
    sourceFields.forEach((sf) => {
      const key = sf.group || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(sf);
    });
    // Sort: contact first, opportunity second, custom third, then any others
    const order = ['contact', 'opportunity', 'custom'];
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [sourceFields]);

  const handleRemove = useCallback(
    (key: number) => {
      onChange(mappings.filter((m) => (m.rowKey !== key)));
    },
    [mappings, onChange]
  );

  const handleChange = useCallback(
    (key: number, field: 'source_field_key' | 'target_field', value: string) => {
      const updated = mappings.map((m) => {
        if (m.rowKey === key) {
          return { ...m, [field]: value };
        }
        return m;
      });
      onChange(updated);
    },
    [mappings, onChange]
  );

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <label className="form-label fw-semibold mb-0">Field Mappings</label>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={onAdd}
        >
          + Add Mapping
        </button>
      </div>

      {mappings.length === 0 && (
        <p className="text-muted small mb-0">
          No field mappings configured. Add mappings to populate project fields from CRM fields.
        </p>
      )}

      {mappings.map((mapping) => (
        <div key={mapping.rowKey} className={classes.mappingRow}>
          <div className={classes.fieldSelect}>
            <select
              className="form-select form-select-sm"
              value={mapping.source_field_key}
              onChange={(e) => handleChange(mapping.rowKey, 'source_field_key', e.target.value)}
            >
              <option value="">Select CRM field...</option>
              {groupedSourceFields.map(([group, fields]) => (
                <optgroup
                  key={group}
                  label={GROUP_LABELS[group] || group}
                >
                  {fields.map((sf) => (
                    <option
                      key={sf.key}
                      value={sf.key}
                      disabled={
                        usedSourceKeys.includes(sf.key)
                        && mapping.source_field_key !== sf.key
                      }
                    >
                      {sf.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <span className={classes.arrow}>&rarr;</span>

          <div className={classes.fieldSelect}>
            <select
              className="form-select form-select-sm"
              value={mapping.target_field}
              onChange={(e) => handleChange(mapping.rowKey, 'target_field', e.target.value)}
            >
              <option value="">Select target field...</option>
              {TARGET_FIELD_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.fields.map((f) => (
                    <option
                      key={f.value}
                      value={f.value}
                      disabled={
                        usedTargetFields.includes(f.value)
                        && mapping.target_field !== f.value
                      }
                    >
                      {f.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={`btn btn-outline-danger btn-sm ${classes.removeButton}`}
            onClick={() => handleRemove(mapping.rowKey)}
            title="Remove mapping"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
}

const FieldMappingEditorMemo = memo(FieldMappingEditor, areEqual);

export { FieldMappingEditorMemo as FieldMappingEditor };
